import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Heart, MagnifyingGlass, Plus, Gift, Sparkle, PencilSimple, TrashSimple, PaperPlaneTilt, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import CustomGiftPreview from "../components/CustomGiftPreview";
import SendGiftSheet from "../components/SendGiftSheet";
import GiftBurst from "../components/GiftBurst";

const SORTS = [
  { id: "popular", label: "Popular" },
  { id: "new", label: "New" },
  { id: "limited", label: "Limited Edition" },
  { id: "price_asc", label: "Price ↑" },
];
const STATUS_STYLE = {
  draft: "bg-white/10 text-cream/70", under_review: "bg-sky-400/20 text-sky-300", submitted: "bg-sky-400/20 text-sky-300",
  approved: "bg-emerald-400/20 text-emerald-300", rejected: "bg-red-500/20 text-red-300", removed: "bg-white/10 text-cream/50",
};

/** GiftShop — browse/search/favorite community gifts, send them, manage your own designs. */
export default function GiftShop() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "mine" ? "mine" : "shop";
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("popular");
  const [q, setQ] = useState("");
  const [sendGift, setSendGift] = useState(null);
  const [burst, setBurst] = useState(null);
  const [replay, setReplay] = useState({});

  const loadShop = useCallback(async () => {
    try {
      const { data: d } = await api.get("/gifts/shop", { params: { category: category || undefined, sort, q: q || undefined, limit: 60 } });
      setData(d);
    } catch (e) { setData({ items: [], categories: [] }); }
  }, [category, sort, q]);

  const loadMine = useCallback(async () => {
    try { const { data: d } = await api.get("/gifts/custom/mine"); setMine(d); } catch { setMine([]); }
  }, []);

  useEffect(() => { const t = setTimeout(loadShop, q ? 250 : 0); return () => clearTimeout(t); }, [loadShop, q]);
  useEffect(() => { if (tab === "mine") loadMine(); }, [tab, loadMine]);

  const fav = async (g) => {
    try {
      const { data: r } = await api.post(`/gifts/${g.id}/favorite`);
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === g.id ? { ...x, is_favorite: r.favorite, favorites_count: (x.favorites_count || 0) + (r.favorite ? 1 : -1) } : x)) }));
    } catch (e) { toast.error("Could not favorite"); }
  };

  const preview = (g) => setBurst({ id: g.id, label: g.name, emoji: g.emoji, waves: g.price_waves, image_url: g.image_url, sound_kind: g.sfx === "none" ? null : g.sfx, custom: { text: g.text, animation: g.animation, colors: g.colors, creator_username: g.creator_username } });

  const remove = async (g) => {
    if (!window.confirm(`Delete “${g.name}”?`)) return;
    try { await api.delete(`/gifts/custom/${g.id}`); loadMine(); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const submit = async (g) => {
    nav(`/gifts/create?id=${g.id}#submit`);
  };

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="gift-shop">
      <header className="sticky top-0 z-30 crystal-glass px-5 pt-6 pb-4">
        <div className="flex items-center gap-3">
          <Link to="/" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-cream" data-testid="giftshop-back"><ArrowLeft size={18} /></Link>
          <div className="flex-1">
            <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Marketplace</p>
            <h1 className="text-cream text-3xl font-black tracking-tighter">Gift Shop</h1>
          </div>
          <Link to="/gifts/create" data-testid="giftshop-create" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase"><Plus size={14} weight="bold" /> Create a Gift</Link>
        </div>
        <div className="mt-4 flex gap-2" data-testid="giftshop-tabs">
          <button onClick={() => setParams({})} data-testid="giftshop-tab-shop" aria-pressed={tab === "shop"} className={`px-4 py-2 rounded-full text-[10px] font-black tracking-[0.2em] uppercase border ${tab === "shop" ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"}`}>Shop</button>
          <button onClick={() => setParams({ tab: "mine" })} data-testid="giftshop-tab-mine" aria-pressed={tab === "mine"} className={`px-4 py-2 rounded-full text-[10px] font-black tracking-[0.2em] uppercase border ${tab === "mine" ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"}`}>My Gifts</button>
        </div>
      </header>

      {tab === "shop" && (
        <div className="px-5 mt-4">
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2.5">
            <MagnifyingGlass size={16} className="text-cream/50" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search gifts or designers…" data-testid="giftshop-search" className="flex-1 bg-transparent text-cream placeholder:text-cream/40 focus:outline-none text-sm" />
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="giftshop-sorts">
            {SORTS.map((s) => (
              <button key={s.id} onClick={() => setSort(s.id)} data-testid={`giftshop-sort-${s.id}`} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border ${sort === s.id ? "bg-jam-gold text-jam-black border-jam-gold" : "bg-white/5 text-cream/70 border-white/10"}`}>{s.label}</button>
            ))}
          </div>
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1" data-testid="giftshop-categories">
            <button onClick={() => setCategory("")} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border ${!category ? "bg-white/20 text-cream border-white/30" : "bg-white/5 text-cream/60 border-white/10"}`}>All</button>
            {(data?.categories || []).map((c) => (
              <button key={c} onClick={() => setCategory(c)} data-testid={`giftshop-cat-${c}`} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border ${category === c ? "bg-white/20 text-cream border-white/30" : "bg-white/5 text-cream/60 border-white/10"}`}>{c}</button>
            ))}
          </div>

          {data === null ? (
            <p className="mt-6 text-cream/40 text-sm">Loading…</p>
          ) : data.items.length === 0 ? (
            <div className="mt-8 crystal-glass rounded-3xl p-6 text-center" data-testid="giftshop-empty">
              <Gift size={36} className="text-papaya mx-auto" weight="duotone" />
              <p className="text-cream font-bold mt-2">No gifts here yet.</p>
              <p className="text-cream/60 text-sm mt-1">Be the first — design a gift and submit it to the marketplace.</p>
              <Link to="/gifts/create" className="inline-block mt-4 px-5 py-2.5 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase">Create a Gift</Link>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-3" data-testid="giftshop-grid">
              {data.items.map((g) => (
                <div key={g.id} className={`rounded-3xl bg-white/5 border p-3 flex flex-col ${g.is_limited ? "border-jam-gold/50" : "border-white/10"}`} data-testid={`shop-gift-${g.id}`}>
                  <div className="relative">
                    <CustomGiftPreview gift={g} size={150} replayKey={replay[g.id] || 0} className="w-full mx-auto" />
                    <button onClick={() => setReplay((r) => ({ ...r, [g.id]: (r[g.id] || 0) + 1 }))} className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-ink/60 text-cream flex items-center justify-center" title="Replay animation"><Play size={12} weight="fill" /></button>
                    {(g.badge || g.is_limited) && <span className="absolute top-2 left-2 bg-jam-gold text-jam-black text-[8px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full">{g.badge || "Limited"}</span>}
                    {g.sold_out && <span className="absolute top-2 right-2 bg-red-500 text-cream text-[8px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full">Sold out</span>}
                  </div>
                  <p className="text-cream font-black text-sm mt-2 truncate">{g.name}</p>
                  <p className="text-cream/50 text-[11px] truncate">by @{g.creator?.username || g.creator_username} · {g.sales_count} sent</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-papaya font-black text-sm" data-testid={`shop-gift-price-${g.id}`}>{g.price_waves} 🪙</span>
                    <button onClick={() => fav(g)} data-testid={`shop-gift-fav-${g.id}`} aria-pressed={g.is_favorite} className={`w-8 h-8 rounded-full flex items-center justify-center ${g.is_favorite ? "bg-pink-500/25 text-pink-300" : "bg-white/10 text-cream/60"}`}><Heart size={14} weight={g.is_favorite ? "fill" : "regular"} /></button>
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    <button onClick={() => preview(g)} data-testid={`shop-gift-preview-${g.id}`} className="flex-1 py-2 rounded-full bg-white/5 border border-white/10 text-cream text-[9px] font-black tracking-[0.15em] uppercase">Preview</button>
                    <button onClick={() => setSendGift(g)} disabled={g.sold_out || g.expired} data-testid={`shop-gift-send-${g.id}`} className="flex-1 py-2 rounded-full bg-papaya text-ink text-[9px] font-black tracking-[0.15em] uppercase flex items-center justify-center gap-1 disabled:opacity-40"><PaperPlaneTilt size={11} weight="fill" /> Send</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "mine" && (
        <div className="px-5 mt-4" data-testid="my-gifts">
          {mine === null ? <p className="text-cream/40 text-sm">Loading…</p> : mine.length === 0 ? (
            <div className="crystal-glass rounded-3xl p-6 text-center">
              <Sparkle size={32} className="text-papaya mx-auto" weight="duotone" />
              <p className="text-cream font-bold mt-2">You haven't designed a gift yet.</p>
              <Link to="/gifts/create" data-testid="mygifts-create" className="inline-block mt-4 px-5 py-2.5 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase">Create your first gift</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {mine.map((g) => (
                <div key={g.id} className="flex items-center gap-3 p-3 rounded-3xl bg-white/5 border border-white/10" data-testid={`my-gift-${g.id}`}>
                  <CustomGiftPreview gift={g} size={84} className="flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-cream font-black truncate">{g.name}</p>
                      <span className={`text-[9px] font-black tracking-[0.15em] uppercase px-2 py-0.5 rounded-full ${STATUS_STYLE[g.status] || STATUS_STYLE.draft}`} data-testid={`my-gift-status-${g.id}`}>{g.status.replace("_", " ")}</span>
                    </div>
                    <p className="text-cream/50 text-[11px]">{g.price_waves} waves · {g.category} · v{g.version}</p>
                    {g.status === "approved" && <p className="text-emerald-300 text-[11px] mt-0.5">{g.sales_count} sent · {g.total_waves} waves in sales · {g.favorites_count || 0} favorites</p>}
                    {g.status === "rejected" && g.rejection_reason && <p className="text-red-300 text-[11px] mt-0.5">{g.rejection_reason}</p>}
                    {g.status === "under_review" && <p className="text-sky-300 text-[11px] mt-0.5">Waiting for Giffwurldaunce review</p>}
                    <div className="flex gap-1.5 mt-2">
                      {(g.status === "draft" || g.status === "rejected") && (
                        <>
                          <button onClick={() => nav(`/gifts/create?id=${g.id}`)} data-testid={`my-gift-edit-${g.id}`} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-cream text-[9px] font-black tracking-[0.15em] uppercase flex items-center gap-1"><PencilSimple size={11} /> Edit</button>
                          <button onClick={() => submit(g)} data-testid={`my-gift-submit-${g.id}`} className="px-3 py-1.5 rounded-full bg-papaya text-ink text-[9px] font-black tracking-[0.15em] uppercase">Submit</button>
                          <button onClick={() => remove(g)} data-testid={`my-gift-delete-${g.id}`} className="px-3 py-1.5 rounded-full bg-red-500/15 text-red-300 text-[9px] font-black tracking-[0.15em] uppercase flex items-center gap-1"><TrashSimple size={11} /></button>
                        </>
                      )}
                      <button onClick={() => preview({ ...g, sfx: g.sfx })} data-testid={`my-gift-preview-${g.id}`} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-cream text-[9px] font-black tracking-[0.15em] uppercase">Preview</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-cream/40 text-[11px] mt-6">Approved gifts are licensed to Giffwurldaunce under the <Link to="/gift-creator-agreement" className="text-papaya underline">Gift Creator Agreement</Link>.</p>
        </div>
      )}

      {sendGift && <SendGiftSheet gift={sendGift} onClose={() => setSendGift(null)} onSent={(g) => setBurst({ ...g, custom: g.custom || { text: sendGift.text, animation: sendGift.animation, colors: sendGift.colors, creator_username: sendGift.creator_username }, waves: sendGift.price_waves })} />}
      {burst && <GiftBurst gift={burst} onDone={() => setBurst(null)} />}
    </div>
  );
}
